import React, { useState, useEffect, useRef, useContext } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import styles from './ChallengeShowPage.scss'
import { DetailsCard } from '../DetailsCard'
import { ChallengeMapCard } from '../ChallengeMap'
import { Leaderboard, messages as leaderboardMessages } from '../Leaderboard'
import { MilestonesCard } from '../MilestonesCard'
import { Rules } from '../Rules'
import { getChallengeId } from '../challengeUtils'
import { REQUIRE_INFO_MODAL } from '../modals/RequireInfoModal'
import { challengeMessages } from '../intlMessages'
import { OrgContext } from 'features/organizations/OrgContext'
import { friendsMessages } from 'features/homepage/Feed'
import { AdminMenuItem } from 'common/GlobalNav/AdminMenu'
import { Loadable } from 'common/Loadable'
import { Distance, Elevation, Duration } from 'common/units'
import { DashedMetric } from 'common/DashedMetric'
import { SHARE_MODAL } from 'common/modals/ShareModal'
import { Card } from 'common/styleguide/Card'
import { CTA } from 'common/styleguide/CTA'
import { SectionBox } from 'common/styleguide/SectionBox'
import { LOGIN_MODAL_OVERLAY } from 'common/styleguide/LoginModalOverlay'
import { Popover } from 'common/styleguide/Popover'
import { ColWrap, MainCol, SecondaryCol } from 'common/layouts/StandardLayout/TwoCol'
import { showModal } from 'lib/modalUtils'
import { useFormatMessage, commonMetrics } from 'lib/localization'
import { getCurrentUser, urlWithPrivacyCode, currentURLWithQueryParams } from 'lib/utils'
import { c } from 'lib/textUtils'
import { useHover } from 'lib/hooks/useHover'
import { goalActions, goalSelectors } from 'data/resources/goals'
import { goalParticipantActions, goalParticipantSelectors } from 'data/resources/goalParticipants'
import { milestoneSelectors } from 'data/resources/milestones'
import { followActions, followSelectors } from 'data/resources/follows'
import { routeSelectors } from 'data/resources/routes'
import { defineMessages } from 'react-intl'
import queryString from 'query-string'

export const messages = defineMessages({
  joinChallenge: {
    id: 'challengeShowPage.joinChallenge',
    defaultMessage: 'Join Challenge'
  },
  challengeJoined: {
    id: 'challengeShowPage.challengeJoined',
    defaultMessage: 'Challenge Joined'
  },
  leaveChallenge: {
    id: 'challengeShowPage.leaveChallenge',
    defaultMessage: 'Leave Challenge'
  },
  communityProgress: {
    id: 'challengeShowPage.communityProgress',
    defaultMessage: 'Community Progress'
  },
  yourProgress: {
    id: 'challengeShowPage.yourProgress',
    defaultMessage: 'Your Progress'
  },
  viewOnLeaderboard: {
    id: 'challengeShowPage.viewOnLeaderboard',
    defaultMessage: 'View on Leaderboard'
  },
  rank: {
    id: 'challengeShowPage.rank',
    defaultMessage: 'Rank'
  },
  complete: {
    id: 'challengeShowPage.complete',
    defaultMessage: 'Complete'
  },
  totalDistance: {
    id: 'challengeShowPage.totalDistance',
    defaultMessage: 'Total Distance'
  },
  totalElevation: {
    id: 'challengeShowPage.totalElevation',
    defaultMessage: 'Total Elevation'
  },
  totalTime: {
    id: 'challengeShowPage.totalTime',
    defaultMessage: 'Total Time'
  },
  noOrgJoin: {
    id: 'challengeShowPage.noOrgJoin',
    defaultMessage: 'You cannot join while signed into the organization'
  }
})

export const _ChallengeShowPage = props => {
  const {
    goal,
    metaForGoal,
    ranks,
    milestones,
    currentUserParticipant,
    follows,
    getRoute,
    preview,
    collapseState
  } = props
  const currentMember = window.rwgps?.config?.currentMember
  const club = window.rwgps?.config?.club
  const isCurrentClub = club?.id === goal.get('clubId')
  const fm = useFormatMessage()
  const [condensed, _setCondensed] = useState(!!ranks.splitIndex && currentUserParticipant.amountCompleted > 0)
  const setCondensed = v => {
    condensed
      ? props.loadParticipants({ page: 0 })
      : props.loadParticipants()
    _setCondensed(v)
  }
  const cuRowRef = useRef(null)

  const commonProps = { goal, ranks, condensed, setCondensed, cuRowRef, fm, ...props }
  const participationType = goal.get('goalParams')['participation_type']

  // the order of these components changes between browser and mobile viewports, so
  // we can't just append the SecondaryCol contents to the MainCol contents like we
  // can elsewhere. hence, this mess
  const collapsed = collapseState === 'viewport'
  const mapComponent = goal.goalParams.route_id && <ChallengeMapCard
    user={props.currentUser}
    clubId={goal.get('clubId')}
    routeId={goal.get('goalParams').route_id}
    participationType={participationType}
    milestones={milestones}
    amountCompleted={participationType === 'community'
      ? metaForGoal.community.completed
      : currentUserParticipant?.amountCompleted}
  />
  const joinComponent = <JoinInvite goal={goal} currentUserParticipant={currentUserParticipant} {...props} fm={fm} />
  const communityProgressComponent = <CommunityProgress goal={goal} community={metaForGoal.community} getRoute={getRoute} fm={fm} />
  const cuProgressComponent = <CUProgress {...commonProps} />
  const milestonesComponent = <MilestonesCard
    goal={goal}
    metaForGoal={metaForGoal}
    milestones={milestones}
    currentUserParticipant={currentUserParticipant}
    preview={preview}
    fm={fm}
  />
  const rulesComponent = <Rules goal={goal} />

  return (
    <OrgContext.Provider value={{ isCurrentClub, currentMember }}>
      <ColWrap>
        <AdminMenuItem
          menuKey='csp_goto_org'
          label='Go to club account'
          onClick={() => { window.location = `/clubs/${goal.get('clubId')}` }} />
        <MainCol>
          <DetailsCard {...commonProps} preview={preview} />
          {!collapsed && mapComponent}
          {collapsed && <>
            {joinComponent}
            {mapComponent}
            {participationType === 'community' && communityProgressComponent}
            {currentUserParticipant && cuProgressComponent}
            {milestones.size > 0 && milestonesComponent}
            {rulesComponent}
          </>}
          <Leaderboard {...commonProps} collapsed={collapsed} follows={follows} noLink={isCurrentClub} preview={preview} />
        </MainCol>
        {!collapsed && <SecondaryCol>
          {joinComponent}
          {participationType === 'community' && communityProgressComponent}
          {currentUserParticipant && cuProgressComponent}
          {milestones.size > 0 && milestonesComponent}
          {rulesComponent}
        </SecondaryCol>}
      </ColWrap>
    </OrgContext.Provider>
  )
}

const JoinInvite = ({ goal, currentUser, currentUserParticipant, joinGoal, leaveGoal, location, fm }) => {
  const [hoverRef, isHovered] = useHover()
  const { isCurrentClub } = useContext(OrgContext)
  const [showClubTip, setShowClubTip] = useState(false)
  const cardRef = useRef(null)
  const joinRef = useRef(null)

  useEffect(() => {
    if (queryString.parse(location.search).autojoin) {
      // we've been redirected here after signup/login-- pop the require info modal
      onJoinLeaveClicked()
    }
  }, [])

  const onJoinLeaveClicked = () => {
    if (isCurrentClub || window.rwgps.config?.club?.id) {
      setShowClubTip(true)
      return
    }

    if (!currentUser.id) {
      showModal(LOGIN_MODAL_OVERLAY, {
        redirectTo: currentURLWithQueryParams({ autojoin: true })
      })
      return
    }

    currentUserParticipant
      ? leaveGoal(currentUserParticipant.id).then(() => window.location.reload())
      : showModal(REQUIRE_INFO_MODAL, {
        currentUserId: currentUser.id,
        goal,
        joinGoal
      })
  }

  return <Card ref={cardRef}>
    <span ref={joinRef}>
      <CTA onClick={onJoinLeaveClicked} margins={false} ref={hoverRef}>
        {currentUserParticipant
          ? fm(isHovered ? messages.leaveChallenge : messages.challengeJoined)
          : fm(messages.joinChallenge)}
      </CTA>
    </span>
    {showClubTip && <Popover
      target={joinRef.current}
      placement='top'
      maxWidth='500px'
      onRequestClose={() => setShowClubTip(false)}>
      {fm(messages.noOrgJoin)}
    </Popover>}
    {goal.visibility === 0 && <CTA
      link
      margins={false}
      onClick={() => showModal(SHARE_MODAL, {
        objectId: goal.id,
        objectType: 'goal',
        objectTitle: goal.name,
        objectUrl: urlWithPrivacyCode(`/challenges/${goal.id}`, goal.privacyCode)
      })}
      style={{ padding: '0px', marginTop: '20px' }}>
      {fm(friendsMessages.inviteFriends)}
    </CTA>}
  </Card>
}

const completedMessage = goalType => {
  switch (goalType) {
    case 'distance':
      return messages.totalDistance
    case 'elevation_gain':
      return messages.totalElevation
    case 'moving_time':
      return messages.totalTime
  }
}
const CommunityProgress = ({ goal, community, getRoute, fm }) => {
  return <SectionBox title={fm(messages.communityProgress)}>
    <div className={styles.cuProgress}>
      <div className={styles.cuStats}>
        <div className={styles.stat}>
          <DashedMetric
            className={styles.metric}
            width='100%'
            value={community.participants}
            label={fm(challengeMessages.participants)}
          />
        </div>
        <div className={styles.stat}>
          <DashedMetric
            className={styles.metric}
            width='100%'
            value={<Completed goalType={goal.goalType} value={community.completed} noLabel />}
            label={fm(completedMessage(goal.goalType))}
          />
        </div>
        <div className={styles.stat}>
          <DashedMetric
            className={styles.metric}
            width='100%'
            value={community.trips}
            label={c(fm(leaderboardMessages.activities, { count: c.trips }))}
          />
        </div>
        {goal.goalParams.has_target && <div className={styles.stat}>
          <DashedMetric
            className={styles.metric}
            width='100%'
            value={`${Math.round((community.completed / community.target) * 100)}%`}
            label={fm(messages.complete)}
          />
        </div>}
      </div>
    </div>
  </SectionBox>
}

const CUProgress = ({ goal, currentUserParticipant, cuRowRef, ranks, condensed, setCondensed, fm }) => {
  const onClick = () => {
    if (currentUserParticipant?.rank > 25) {
      if (!condensed && !cuRowRef.current) {
        setCondensed(true)
      }
    }
    cuRowRef.current && cuRowRef.current.scrollIntoView()
  }

  const [C, label] = fmtAndLabel(goal.goalType, currentUserParticipant.amountCompleted, fm)
  return <SectionBox title={fm(messages.yourProgress)}>
    <div className={styles.cuProgress}>
      <div className={styles.cuStats}>
        <div className={styles.stat}>
          <DashedMetric
            width='100%'
            className={styles.metric}
            value={currentUserParticipant.amountCompleted > 0 ? currentUserParticipant.rank : 0}
            label={fm(messages.rank)}
          />
        </div>
        <div className={styles.stat}>
          <DashedMetric
            width='100%'
            className={styles.metric}
            value={C}
            label={label}
          />
        </div>
        <div className={styles.stat}>
          <DashedMetric
            width='100%'
            className={styles.metric}
            value={currentUserParticipant.trips}
            label={c(fm(leaderboardMessages.activities, { count: currentUserParticipant.trips }))}
          />
        </div>
        {goal.get('goalParams').participation_type !== 'community' && goal.get('goalParams').has_target && <div className={styles.stat}>
          <DashedMetric
            width='100%'
            className={styles.metric}
            value={currentUserParticipant.goalParams ? currentUserParticipant.goalParams.progress_text : '0'}
            label={fm(messages.complete)}
          />
        </div>}
      </div>
      {currentUserParticipant?.amountCompleted > 0 && <a onClick={onClick} className={styles.viewOnLeaderboard}>{fm(messages.viewOnLeaderboard)}</a>}
    </div>
  </SectionBox>
}

const fmtAndLabel = (goalType, amountCompleted, fm) => {
  const C = <Completed goalType={goalType} value={amountCompleted} noLabel />

  if (goalType === 'distance') {
    return [C, fm(commonMetrics.distance)]
  }
  if (goalType === 'elevation_gain') {
    return [C, fm(commonMetrics.elevation)]
  }
  if (goalType === 'moving_time') {
    return [C, fm(commonMetrics.time)]
  }
}

export const Completed = ({ goalType, value, noLabel, fm }) => {
  if (goalType === 'distance') {
    return <>
      <Distance short value={value} />
      {!noLabel && <span>{fm(commonMetrics.distance)}</span>}
    </>
  }
  if (goalType === 'elevation_gain') {
    return <>
      <Elevation short value={value} />
      {!noLabel && <span>{fm(commonMetrics.elevation)}</span>}
    </>
  }
  if (goalType === 'moving_time') {
    return <>
      <Duration biggestUnits value={value} />
      {!noLabel && <span>{fm(commonMetrics.time)}</span>}
    </>
  }
}

const LoadableChallengeShowPage = ({ currentUser, loadGoal, loadFollows, ...props }) => {
  const resourceLoaded = () => Promise.all([
    loadGoal(),
    props.loadParticipants(),
    currentUser?.id && loadFollows(currentUser.id)
  ])

  return <Loadable
    component={_ChallengeShowPage}
    resourceLoaded={resourceLoaded}
    currentUser={currentUser}
    {...props}
  />
}

const mapStateToProps = (state, ownProps) => {
  const challengeId = getChallengeId(ownProps)
  const currentUser = getCurrentUser()
  const preview = queryString.parse(ownProps.location.search)
  return {
    currentUser,
    goal: goalSelectors.find(state, challengeId),
    metaForGoal: goalSelectors.metaForGoal(state, challengeId),
    participant: pid => goalParticipantSelectors.find(state, pid),
    participants: goalParticipantSelectors.byGoalId(state, challengeId),
    currentUserParticipant: goalParticipantSelectors.forUserByGoal(state, currentUser?.id, challengeId),
    ranks: goalParticipantSelectors.ranksForGoal(state, challengeId),
    milestones: milestoneSelectors.byGoalId(state, challengeId),
    follows: followSelectors.followsForUser(state, currentUser?.id).filter(f => f.itemType === 'User'),
    followedParticipants: follows => goalParticipantSelectors.byUserIdsAndGoal(
      state,
      follows.map(f => f.itemId),
      challengeId
    ),
    getRoute: rid => routeSelectors.find(state, rid),
    preview: preview === 'true'
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const goalId = getChallengeId(ownProps)
  const privacyCode = queryString.parse(ownProps.location.search).privacy_code
  return {
    loadGoal: () => dispatch(goalActions.read(goalId, { extras: 'milestones,current_participant,route', privacyCode })),
    loadParticipants: paginationParams => dispatch(goalParticipantActions.list({ goalId, privacyCode, ...paginationParams }, { goalId })),
    loadFollows: userId => dispatch(followActions.list({ userId })),
    joinGoal: () => dispatch(goalActions.join(goalId, { privacyCode })),
    leaveGoal: participantId => dispatch(goalParticipantActions.delete(participantId))
  }
}

export const ChallengeShowPage = withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(LoadableChallengeShowPage))
